import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import I18n from "i18n-js";
import { Button } from "@mex/mex-web-ui";
import "./index.less";

import LeftFinanceImg from "./assets/left-finance.svg";
import LeftGreenImg from "./assets/left-green.svg";
import LeftInsightsImg from "./assets/left-insights.svg";
import LeftMenuImg from "./assets/left-menu.svg";
import LeftYellowImg from "./assets/left-yellow.svg";
import LeftHomeImg from "./assets/left-home.svg";
import XSvg from "./assets/x.svg";
import LeftFreeCreditImg from "./assets/left-freecredit.svg";
import { directToURl, ADSUMP, isRoleCashier } from "../utils";
import { haveAccessToFeature, getFlagValue } from "../utils/FeatureHelper";
import { hasBannerUpsell, hasBannerFreeCredit } from "../constants";
import { getFreeCreditInfo } from './fc-util';
import { generateUTMParams } from './util';

const DISMISS_LS_KEY = "__DISMISSED_AT__";

const isSameDay = (date1, date2) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const hasDismissedToday = () => {
  const dismissedAt = new Date(
    parseInt(window.localStorage.getItem(DISMISS_LS_KEY) || "0", 10)
  );
  const now = new Date();
  return isSameDay(dismissedAt, now);
};

const useOpenStatus = (isShowFcBanner) => {
  // will show banner if either has upsell or free credit
  const initialOpen =
    (haveAccessToFeature(hasBannerUpsell) || isShowFcBanner) &&
    !isRoleCashier() &&
    !hasDismissedToday();
  const [open, setOpen] = useState(initialOpen);

  return [open, setOpen];
};

export const MarketingBanner = (props) => {
  const { className, onClick, onClose, onOpen, tab, trackEvent } =
    props;
    
  const { currency, amount } = getFreeCreditInfo();
  const isShowFcBanner = amount && getFlagValue(hasBannerFreeCredit) && getFlagValue(hasBannerFreeCredit) !== '0';

  // free credit banner has the high priority 
  const variant = isShowFcBanner ? 'freecredit' : props.variant
  const eventType = variant === 'freecredit' ? 'freecredit' : 'upsell';
  const [open, setOpen] = useOpenStatus(isShowFcBanner);

  const { title, description, leftImg, cta } = {
    title: {
      home: I18n.t("apt_mm_acquisition_heading_boost_your_sales_banners"),
      finance: I18n.t("apt_mm_acquisition_heading_ready_for_more_banners"),
      menu: I18n.t("apt_mm_acquisition_heading_adding_new_items_banners"),
      insights: I18n.t("apt_mm_acquisition_heading_get_biz_booming_banners"),
      green: I18n.t("apt_mm_acquisition_heading_get_seen_banners"),
      yellow: I18n.t("apt_mm_acquisition_heading_grow_your_profile_banners"),
      freecredit: I18n.t("apt_free_ad_credit_heading_free_credit_awaits", {
        currency,
        amount,
        defaultValue: '{{currency}}{{amount}} FREE ad credit awaits!'
      }),
    }[variant],

    description: {
      home: I18n.t("apt_mm_acquisition_body_take_biz_next_level_banners"),
      finance: I18n.t("apt_mm_acquisition_body_keep_sales_rolling_banners"),
      menu: I18n.t("apt_mm_acquisition_body_spread_good_news_rolling_banners"),
      insights: I18n.t("apt_mm_acquisition_body_grow_sales_banners"),
      green: I18n.t("apt_mm_acquisition_body_improve_biz_performance_banners"),
      yellow: I18n.t(
        "apt_mm_acquisition_body_attract_more_customers_rolling_banners"
      ),
      freecredit: I18n.t("apt_free_ad_credit_body_take_action", {
        defaultValue: 'Take action now by creating and publishing your campaign.'
      }),
    }[variant],

    leftImg: {
      home: LeftHomeImg,
      finance: LeftFinanceImg,
      menu: LeftMenuImg,
      insights: LeftInsightsImg,
      green: LeftGreenImg,
      yellow: LeftYellowImg,
      freecredit: LeftFreeCreditImg
    }[variant],

    cta: {
      freecredit: I18n.t("apt_free_ad_credit_button_claim_now", {
        defaultValue: 'Claim Now'
      })
    }[variant]
  };

  const _onClose = useCallback(() => {
    if (tab && trackEvent) {
      trackEvent(
        "MARKETING_BANNER",
        "PRODUCT_UPSELL.BANNER_DISMISS",
        JSON.stringify({
          portal_tab: tab,
          status: "Success",
          error_msg: null,
          type: eventType
        })
      );
    }
    setOpen(false);
    window.localStorage.setItem(DISMISS_LS_KEY, new Date().getTime());
    if (onClose) {
      onClose();
    }
  }, [onClose, setOpen]);

  const _onClick = useCallback(() => {
    if (tab && trackEvent) {
      trackEvent(
        "MARKETING_BANNER",
        "PRODUCT_UPSELL.BANNER_CLICK",
        JSON.stringify({
          portal_tab: tab,
          status: "Success",
          error_msg: null,
          type: eventType
        })
      );
    }
    if (onClick) {
      onClick();
    }

    if (tab === 'home') {
      const params = generateUTMParams('home');
      directToURl(null, ADSUMP, params);
    } else {
      directToURl(null, ADSUMP);
    }
  }, [onClick, tab, trackEvent]);

  const viewTracked = useRef(false);
  useEffect(() => {
    if (open && tab && trackEvent && !viewTracked.current) {
      viewTracked.current = true;
      trackEvent(
        "MARKETING_BANNER",
        "PRODUCT_UPSELL.BANNER_VIEW",
        JSON.stringify({ portal_tab: tab, type: eventType }),
      );
    }
  }, [open, tab, trackEvent]);

  useEffect(() => {
    if (open && onOpen) {
      onOpen();
    }
  }, [open, onOpen]);

  if (!open) return null;

  return (
    <div
      className={`troy-marketing-banner troy-marketing-banner--${variant} ${className}`}
    >
      <img className="troy-marketing-banner__img" src={leftImg} />

      <img
        className="troy-marketing-banner__close"
        src={XSvg}
        onClick={_onClose}
      />

      <div className="troy-marketing-banner__text-container">
        <p className="troy-marketing-banner__header">{title}</p>
        <p className="troy-marketing-banner__description">{description}</p>
      </div>

      <Button
        variant="ghost"
        size="small"
        className={`troy-marketing-banner__cta troy-marketing-banner__cta--${variant}`}
        onClick={_onClick}
      >
        {cta || I18n.t("apt_mm_acquisition_button_try_marketing_manager")}
      </Button>

      <svg
        className={`troy-marketing-banner__imgr troy-marketing-banner__imgr--${variant}`}
      >
        <circle cx={280} cy={200} r={89} fill="currentColor" />
      </svg>
    </div>
  );
};

MarketingBanner.propTypes = {
  className: PropTypes.string,

  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,

  variant: PropTypes.oneOf([
    "home",
    "finance",
    "menu",
    "insights",
    // these 2 do not belong to any page yet.
    "green",
    "yellow",
    "freecredit"
  ]),

  tab: PropTypes.oneOf(["home", "finance", "menu", "insights"]),

  trackEvent: PropTypes.func,
};

MarketingBanner.defaultProps = {
  variant: "home",
};
